import { Button } from '@/components/ui/button'
import { DrawerClose, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'

import type { ConnectionRowPresentation } from './types'

interface ConnectionRecordPanelProps {
	onClose: () => void
	connectionRow?: ConnectionRowPresentation
	isNew?: boolean
}

function CloseButton({ onClick }: { onClick: () => void }) {
	return (
		<DrawerClose asChild>
			<Button
				variant="ghost"
				size="icon"
				onClick={onClick}
				className="hover:bg-primary hover:text-primary-foreground"
				aria-label="Close"
			>
				<X className="h-4 w-4" />
			</Button>
		</DrawerClose>
	)
}

export function ConnectionRecordPanel({
	onClose,
	connectionRow,
	isNew = false,
}: ConnectionRecordPanelProps) {
	if (isNew) {
		return (
			<div className="flex flex-col h-full">
				<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DrawerTitle>Add Connection</DrawerTitle>
					<CloseButton onClick={onClose} />
				</DrawerHeader>

				<div className="flex-1 px-4 overflow-auto">
					<div className="space-y-2 text-sm text-muted-foreground">
						Create a connection to a third-party platform. This form is coming soon.
					</div>
				</div>
			</div>
		)
	}

	if (!connectionRow) {
		return (
			<div className="flex flex-col h-full">
				<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
					<DrawerTitle>Connection</DrawerTitle>
					<CloseButton onClick={onClose} />
				</DrawerHeader>

				<div className="flex-1 px-4 overflow-auto">
					<div className="text-sm text-muted-foreground">Select a connection to view details.</div>
				</div>
			</div>
		)
	}

	const { connection } = connectionRow

	return (
		<div className="flex flex-col h-full">
			<DrawerHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
				<DrawerTitle>
					<div className="flex items-center gap-3">
						{connectionRow.logoSrc ? (
							<img
								src={connectionRow.logoSrc}
								alt={`${connectionRow.platform} logo`}
								className="h-5 w-5"
							/>
						) : (
							<div className="h-5 w-5 rounded bg-muted flex items-center justify-center text-xs font-medium">
								{connectionRow.platform.charAt(0).toUpperCase()}
							</div>
						)}
						{connectionRow.name}
					</div>
				</DrawerTitle>
				<CloseButton onClick={onClose} />
			</DrawerHeader>

			<div className="flex-1 px-4 overflow-auto">
				<div className="grid grid-cols-2 gap-4">
					<div>
						<Label>Platform</Label>
						<p className="text-sm font-medium capitalize">{connectionRow.platform.replace('_', ' ')}</p>
					</div>
					<div>
						<Label>Owner</Label>
						<p className="text-sm font-medium capitalize">{connectionRow.owner}</p>
					</div>
					<div>
						<Label>Status</Label>
						<p className="text-sm font-medium capitalize">{connectionRow.status}</p>
					</div>
					{connection && (
						<>
							<div>
								<Label>Auth Type</Label>
								<p className="text-sm font-medium capitalize">
									{connection.auth_type.replace('_', ' ')}
								</p>
							</div>
							<div>
								<Label>Last Synced</Label>
								<p className="text-sm">
									{connection.last_synced_at
										? new Date(connection.last_synced_at).toLocaleString()
										: 'Never'}
								</p>
							</div>
							<div>
								<Label>Created</Label>
								<p className="text-sm">{new Date(connection.created_at).toLocaleString()}</p>
							</div>
							{connection.expires_at && (
								<div>
									<Label>Expires</Label>
									<p className="text-sm">{new Date(connection.expires_at).toLocaleString()}</p>
								</div>
							)}
						</>
					)}
				</div>

				{connectionRow.managed && (
					<div className="mt-4 pt-4 border-t">
						<Button variant="outline" className="w-full">
							Connect Your Own {connectionRow.platform}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
